import React from "react";

// =============================================================================
// Customizable filter registry + presentational components for Bulk Edit.
//
// One data-driven registry (FILTER_DEFS) describes every filter the panel can
// show; the parent owns which are *active* (persisted) and renders them through
// the memoized <FilterPanel>. The Add Filter modal body is <FilterPickerBody>.
//
// Each def is fully self-describing so there is no per-id switch:
//   control: "text-op" | "num-op" | "select"
//   - text-op / num-op: { typeKey, valueKey, placeholder }
//   - select: { valueKey, placeholder, optionsKey? (dynamic) | options? (static) }
//   valueKey  — field whose presence means "this filter has a value"
//   resetKeys — every field cleared when the filter is removed
//   permKey   — gates pricing filters behind a view permission
// =============================================================================

export const FILTER_GROUP_ORDER = ["Text", "Taxonomy", "Pricing", "Inventory", "Status"];

const TEXT_OPERATORS = [
  { value: "1", label: "contains" },
  { value: "2", label: "is exactly" },
  { value: "3", label: "does not contain" },
  { value: "4", label: "start with" },
  { value: "5", label: "end with" },
];

const NUM_OPERATORS = [
  { value: ">", label: ">" },
  { value: "<", label: "<" },
  { value: "=", label: "=" },
  { value: ">=", label: ">=" },
  { value: "<=", label: "<=" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "1", label: "Active" },
  { value: "2", label: "Inactive" },
];

const B2B_FILTER_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const FILTER_DEFS = [
  { id: "title",          label: "Title",          group: "Text",      control: "text-op", typeKey: "title_filter_type",   valueKey: "title_filter_value",   placeholder: "Enter title...",     resetKeys: ["title_filter_type", "title_filter_value"] },
  { id: "barcode",        label: "Barcode",        group: "Text",      control: "text-op", typeKey: "barcode_filter_type", valueKey: "barcode_filter_value", placeholder: "Enter barcode...",   resetKeys: ["barcode_filter_type", "barcode_filter_value"] },
  { id: "category",       label: "Category",       group: "Taxonomy",  control: "select",  valueKey: "category",           placeholder: "Select Category",     optionsKey: "category",     resetKeys: ["category", "sub_category"] },
  { id: "sub_category",   label: "Sub Category",   group: "Taxonomy",  control: "select",  valueKey: "sub_category",       placeholder: "Select Sub Category", optionsKey: "sub_category", resetKeys: ["sub_category"] },
  { id: "brand",          label: "Brand",          group: "Taxonomy",  control: "select",  valueKey: "brand",              placeholder: "Select Brand",        optionsKey: "brand",        resetKeys: ["brand"] },
  { id: "type",           label: "Type",           group: "Taxonomy",  control: "select",  valueKey: "type",               placeholder: "Select Type",         optionsKey: "type",         resetKeys: ["type"] },
  { id: "regular_price",  label: "Regular Price",  group: "Pricing",   control: "num-op",  typeKey: "regular_price_filter_type", valueKey: "regular_price_value", placeholder: "Amount", permKey: "canSeeRegularPrice", resetKeys: ["regular_price_filter_type", "regular_price_value"] },
  { id: "cost",           label: "Cost Price",     group: "Pricing",   control: "num-op",  typeKey: "cost_filter_type",          valueKey: "cost_value",          placeholder: "Amount", permKey: "canSeeCost",        resetKeys: ["cost_filter_type", "cost_value"] },
  { id: "sale_price",     label: "Sale Price",     group: "Pricing",   control: "num-op",  typeKey: "sale_price_filter_type",    valueKey: "sale_price_value",    placeholder: "Amount", permKey: "canSeeSalePrice",    resetKeys: ["sale_price_filter_type", "sale_price_value"] },
  { id: "stock_quantity", label: "Stock Quantity", group: "Inventory", control: "num-op",  typeKey: "stock_quantity_filter_type", valueKey: "stock_quantity_value", placeholder: "Enter quantity...", resetKeys: ["stock_quantity_filter_type", "stock_quantity_value"] },
  { id: "position",       label: "Position",       group: "Inventory", control: "num-op",  typeKey: "position_filter_type",       valueKey: "position_value",       placeholder: "Enter position...", resetKeys: ["position_filter_type", "position_value"] },
  { id: "status",         label: "Status",         group: "Status",    control: "select",  valueKey: "status",             placeholder: "Select Status", options: STATUS_FILTER_OPTIONS, resetKeys: ["status"] },
  { id: "b2b",            label: "B2B",            group: "Status",    control: "select",  valueKey: "b2b",                placeholder: "All",           options: B2B_FILTER_OPTIONS,   resetKeys: ["b2b"] },
];

export const ACTIVE_FILTERS_KEY = "bulkEditActiveFilters";
// First-load set (before any customization). Toolbar Search is always available
// separately, so the panel just opens with Category + Status.
export const DEFAULT_ACTIVE_FILTER_IDS = ["category", "status"];

// Filters the user is permitted to see. `perms` is the bag of canSee* booleans.
export const getAvailableFilterDefs = (perms) =>
  FILTER_DEFS.filter((d) => !d.permKey || perms[d.permKey]);

const OperatorRow = ({ def, operators, inputType, filters, onChange }) => (
  <div className="woo-filter-row">
    <select
      className="form-select"
      name={def.typeKey}
      value={filters[def.typeKey]}
      onChange={onChange}
      aria-label={`${def.label} operator`}
    >
      {operators.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <input
      className="form-control"
      type={inputType}
      name={def.valueKey}
      value={filters[def.valueKey]}
      onChange={onChange}
      placeholder={def.placeholder}
    />
  </div>
);

// One filter card (label + control + remove). Memoized so an unrelated parent
// re-render (product edits, the relative-time tick) doesn't touch it.
export const FilterField = React.memo(function FilterField({
  def,
  filters,
  optionSources,
  onChange,
  onRemove,
}) {
  const isActive = !!filters[def.valueKey];

  let control = null;
  if (def.control === "text-op") {
    control = <OperatorRow def={def} operators={TEXT_OPERATORS} inputType="text" filters={filters} onChange={onChange} />;
  } else if (def.control === "num-op") {
    control = <OperatorRow def={def} operators={NUM_OPERATORS} inputType="number" filters={filters} onChange={onChange} />;
  } else {
    const opts = def.options || optionSources[def.optionsKey] || [];
    control = (
      <select
        className="form-select"
        name={def.valueKey}
        value={filters[def.valueKey]}
        onChange={onChange}
      >
        <option value="">{def.placeholder}</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className={`woo-filter-item${isActive ? " is-active" : ""}`}>
      <label>{def.label}</label>
      {control}
      <button
        type="button"
        className="woo-filter-item__remove"
        title={`Remove ${def.label} filter`}
        aria-label={`Remove ${def.label} filter`}
        onClick={() => onRemove(def)}
      >
        <i className="bi bi-x" aria-hidden="true" />
      </button>
    </div>
  );
});

// The active-filter grid. Memoized; `activeDefs`, `filters`, `optionSources`
// and the handlers are referentially stable across product-grid re-renders, so
// this whole subtree is skipped unless a filter actually changes.
export const FilterPanel = React.memo(function FilterPanel({
  activeDefs,
  filters,
  optionSources,
  onChange,
  onRemove,
  onKeyDown,
}) {
  return (
    <div className="woo-filters-grid" onKeyDown={onKeyDown}>
      {activeDefs.map((def) => (
        <FilterField
          key={def.id}
          def={def}
          filters={filters}
          optionSources={optionSources}
          onChange={onChange}
          onRemove={onRemove}
        />
      ))}
      {activeDefs.length === 0 && (
        <div className="woo-filters-empty">
          No filters added yet. Click <strong>Add Filter</strong> below to choose
          which filters appear here.
        </div>
      )}
    </div>
  );
});

// Body of the Add Filter modal: searchable, grouped, horizontal picker.
export const FilterPickerBody = React.memo(function FilterPickerBody({
  availableDefs,
  activeFilterIds,
  search,
  onSearchChange,
  onToggle,
}) {
  const q = search.trim().toLowerCase();
  return (
    <div className="modal-body filter-picker">
      <input
        type="text"
        className="form-control filter-picker__search"
        placeholder="Search filters…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        aria-label="Search filters"
      />
      <div className="filter-picker__groups">
        {FILTER_GROUP_ORDER.map((group) => {
          const defs = availableDefs.filter(
            (d) => d.group === group && d.label.toLowerCase().includes(q)
          );
          if (defs.length === 0) return null;
          return (
            <div key={group} className="filter-picker__group">
              <div className="filter-picker__group-title">{group}</div>
              <div className="filter-picker__items">
                {defs.map((def) => {
                  const isSelected = activeFilterIds.has(def.id);
                  return (
                    <label
                      key={def.id}
                      className={`filter-picker__item${isSelected ? " is-selected" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected}
                        onChange={() => onToggle(def)}
                      />
                      <span className="filter-picker__item-label">{def.label}</span>
                      {isSelected && (
                        <i className="bi bi-check-circle-fill" aria-hidden="true" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
